<template>
  <Modal
    :show="show"
    width="366"
    cancel-text=""
    action-text=""
    @close="handleClose"
  >
    <template #title>
      <span class="text-h2">Create Job Family Group</span>
    </template>
    <template #content>
      <p class="text-body-1 text-textDarkest mb-4">
        Give the Job Family Group a unique name.
      </p>
      <v-text-field
        v-model="name"
        placeholder="Job Family Group Name"
        variant="outlined"
        density="compact"
        rounded="lg"
        color="primary"
        :error="Boolean(nameError)"
        :error-messages="nameError"
        hide-details="auto"
        class="create-job-family-group-field mt-4 mb-14"
      />
    </template>
    <template #actions>
      <DpplNewButton
        class="bulk-modal-action text-button-1"
        variant="flat"
        color="primary"
        :disabled="!canCreate"
        :loading="loading"
        @click="handleConfirm"
      >
        Create
      </DpplNewButton>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import Modal from '@/components/shared/Modal.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import {
  getArchitectureNameError,
  isArchitectureNameValid,
  JOB_FAMILY_GROUP_NAME_DUPLICATE_MESSAGE,
} from '@/helpers/jobProfileSettingsValidation';

const props = defineProps({
  show: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  existingNames: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'confirm']);

const name = ref('');

const trimmedName = computed(() => name.value.trim());

const nameError = computed(() => getArchitectureNameError(
  name.value,
  props.existingNames,
  JOB_FAMILY_GROUP_NAME_DUPLICATE_MESSAGE,
));

const canCreate = computed(() => isArchitectureNameValid(
  name.value,
  props.existingNames,
  JOB_FAMILY_GROUP_NAME_DUPLICATE_MESSAGE,
));

const reset = () => {
  name.value = '';
};

const handleClose = () => {
  reset();
  emit('close');
};

const handleConfirm = () => {
  if (!canCreate.value) {
    return;
  }

  emit('confirm', trimmedName.value);
};

watch(() => props.show, (isOpen) => {
  if (!isOpen) {
    reset();
  }
});
</script>

<style scoped lang="less">
.bulk-modal-action {
  width: 150px;
  height: 40px !important;
  min-height: 40px !important;
}

.create-job-family-group-field {
  :deep(.v-field__input) {
    font-size: 13px;
  }

  :deep(.v-field__input::placeholder) {
    color: @text-lighter;
    opacity: 1;
  }

  :deep(.v-input__details) {
    padding-inline: 0;
  }

  :deep(.v-messages__message) {
    font-size: 11px;
    line-height: 1.5;
  }
}
</style>
